import React from "react";
import { Receipt, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import * as api from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export const REBILL_OPTIONS = [
    { value: "none", label: "Non applicable" },
    { value: "pending", label: "À refacturer" },
    { value: "done", label: "Refacturée" },
];
export const REBILL_LABELS = Object.fromEntries(REBILL_OPTIONS.map((o) => [o.value, o.label]));
const STYLES = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    done: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export const rebillStatus = (item) =>
    item?.rebill_status || ((item?.rebill_department || item?.rebill_amount || item?.rebill_date) ? "pending" : "none");

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-CH") : "");

export function rebillSummary(item) {
    const parts = [];
    if (item.rebill_department) parts.push(item.rebill_department);
    if (item.rebill_amount) parts.push(`${item.rebill_amount} CHF`);
    return parts.join(" · ");
}

export function RebillBadge({ item, testId, compact = false }) {
    const status = rebillStatus(item);
    if (status === "none") return <span className="text-gray-300" data-testid={testId}>—</span>;
    const dateTxt = status === "done" && item.rebill_date ? `le ${fmtDate(item.rebill_date)}` : "";
    const tooltip = [REBILL_LABELS[status], dateTxt, rebillSummary(item)].filter(Boolean).join(" — ");
    return (
        <span className="inline-flex flex-col items-start gap-0.5" data-testid={testId} data-rebill-status={status} title={tooltip}>
            <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium border whitespace-nowrap ${STYLES[status]}`}>
                <Receipt className="w-2.5 h-2.5" />{REBILL_LABELS[status]}{!compact && dateTxt ? ` · ${fmtDate(item.rebill_date)}` : ""}
            </span>
            {!compact && rebillSummary(item) && <span className="text-[10px] text-gray-500 whitespace-nowrap">{rebillSummary(item)}</span>}
            {compact && (item.rebill_department || dateTxt) && <span className="text-[9px] text-gray-400 whitespace-nowrap max-w-[110px] truncate">{item.rebill_department || dateTxt}</span>}
        </span>
    );
}

// Bouton rapide « Valider la refacturation » / « Rouvrir »
export function RebillActionButton({ item, onChanged, size = "sm", variant = "outline", className = "" }) {
    const status = rebillStatus(item);
    if (status === "none") return null;
    const run = async (e) => {
        e?.stopPropagation?.();
        try {
            const updated = status === "pending" ? await api.confirmInventoryRebill(item.id) : await api.reopenInventoryRebill(item.id);
            toast.success(status === "pending" ? "Refacturation validée" : "Refacturation remise à faire");
            onChanged?.(updated);
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    };
    return status === "pending" ? (
        <Button type="button" size={size} variant={variant} onClick={run} className={`text-emerald-700 border-emerald-200 hover:bg-emerald-50 ${className}`} title="Valider : ce matériel a été refacturé" data-testid={`rebill-confirm-${item.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Valider la refacturation
        </Button>
    ) : (
        <Button type="button" size={size} variant="ghost" onClick={run} className={`text-gray-500 ${className}`} title="Remettre la refacturation à faire" data-testid={`rebill-reopen-${item.id}`}>
            <RotateCcw className="w-3.5 h-3.5 mr-1" />Rouvrir
        </Button>
    );
}

// Section de formulaire : statut (optionnel) + champs affichés seulement si applicable
export function RebillFields({ form, set, testPrefix = "eq" }) {
    const status = form.rebill_status || "none";
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap" data-testid={`${testPrefix}-rebill-status-group`}>
                {REBILL_OPTIONS.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => set("rebill_status", o.value)}
                        data-testid={`${testPrefix}-rebill-status-${o.value}`}
                        className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                            status === o.value ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"
                        }`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
            {status !== "none" && (
                <div className="grid grid-cols-3 gap-3">
                    <div><label className="form-label text-xs">Département refacturé</label><Input value={form.rebill_department || ""} onChange={(e) => set("rebill_department", e.target.value)} data-testid={`${testPrefix}-rebill-dept`} /></div>
                    <div><label className="form-label text-xs">Montant (CHF)</label><Input type="number" step="0.01" value={form.rebill_amount ?? ""} onChange={(e) => set("rebill_amount", e.target.value)} data-testid={`${testPrefix}-rebill-amount`} /></div>
                    <div><label className="form-label text-xs">{status === "done" ? "Date de refacturation" : "Date prévue (optionnel)"}</label><Input type="date" value={form.rebill_date || ""} onChange={(e) => set("rebill_date", e.target.value)} data-testid={`${testPrefix}-rebill-date`} /></div>
                </div>
            )}
        </div>
    );
}
